import { ParsedQuery } from 'query-string';
import { useCallback, useEffect } from 'react';
import { Transformer, useHash } from 'hooks/use-hash';
import { logger } from 'services';
import { useDocuments } from '../../document-store';

type Active = ['active'];
type InActive = ['in-active'];

type RevisionParams = Active | InActive;

export const transformer: Transformer<RevisionParams> = {
  from: (parsed) => {
    if (typeof parsed.revisions !== 'string') {
      return ['in-active'];
    }

    return ['active'];
  },
  to: (params): ParsedQuery => {
    switch (params[0]) {
      case 'active':
        return {
          revisions: 'active',
        };
      default:
        return {
          revisions: null,
        };
    }
  },
};

export const useRevisionsToggle = (
  {
    waitForEditor,
  }: {
    waitForEditor: boolean;
  } = { waitForEditor: true }, // override the flag to listen the the url-hash and not the editor
) => {
  const [params, setParams] = useHash(transformer);
  const [state] = useDocuments();

  const showRevisionEditor = useCallback(() => {
    setParams(['active']);
  }, [setParams]);

  const hideRevisionEditor = useCallback(() => {
    try {
      state?.editor?.config
        ?.get('revisionHistory')
        ?.closeRevisionViewerCallback();
      setParams(['in-active']);

      return Promise.resolve();
    } catch (error) {
      logger.warn('Error in destroying revisions editor', { error });

      return Promise.resolve();
    }
  }, [setParams, state?.editor?.config]);

  useEffect(() => {
    if (!waitForEditor) {
      return;
    }

    if (params[0] === 'active' && state.editor) {
      showRevisionEditor();

      return;
    }

    try {
      hideRevisionEditor();
    } catch (error) {
      logger.error('Error in hiding revision editor', { error });
    }
  }, [
    hideRevisionEditor,
    params,
    showRevisionEditor,
    state.editor,
    waitForEditor,
  ]);

  return {
    isRevisionsActive: params[0] === 'active',
    showRevisionEditor,
    hideRevisionEditor,
  };
};
